import React, { createContext, useContext, useState, useCallback, ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { gql } from "~/__generated__"
import { InvitationByTokenQuery } from "~/__generated__/graphql"
import { contactsPath } from "~/common/paths"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { useToast } from "~/ui/use-toast"
import { getGraphqlErrorMessage } from "~/ui/errors"

type Invitation = NonNullable<InvitationByTokenQuery["invitationByToken"]>

export const INVITATION_ACCEPT_MUTATION = gql(`
  mutation InvitationAccept($input: InvitationAcceptInput!) {
    invitationAccept(input: $input) {
      contact {
        id
        user {
          id
          fullName
          handle
        }
        owner {
          id
          fullName
        }
      }
      invitation {
        id
        title
        contactsAddedCount
      }
    }
  }
`)

interface InvitationContextType {
  invitation: Invitation | null
  invitationToken: string | null
  setInvitation: (invitation: Invitation | null, token: string | null) => void
  clearInvitation: () => void
  hasInvitation: boolean
}

interface UseAcceptInvitationOptions {
  onSuccess?: (result: any, inviterName: string) => void
  onError?: (error: any) => void
  mode?: "standard" | "onboarding"
  redirectOnError?: boolean
}

const InvitationContext = createContext<InvitationContextType | undefined>(undefined)

interface InvitationProviderProps {
  children: ReactNode
}

export const InvitationProvider: React.FC<InvitationProviderProps> = ({ children }) => {
  const [invitation, setInvitationState] = useState<Invitation | null>(null)
  const [invitationToken, setInvitationToken] = useState<string | null>(null)

  const setInvitation = useCallback((invitation: Invitation | null, token: string | null) => {
    setInvitationState(invitation)
    setInvitationToken(token)
  }, [])

  const clearInvitation = useCallback(() => {
    setInvitationState(null)
    setInvitationToken(null)
  }, [])

  const hasInvitation = invitation !== null && invitationToken !== null

  const value: InvitationContextType = {
    invitation,
    invitationToken,
    setInvitation,
    clearInvitation,
    hasInvitation,
  }

  return <InvitationContext.Provider value={value}>{children}</InvitationContext.Provider>
}

export const useInvitation = (): InvitationContextType => {
  const context = useContext(InvitationContext)
  if (context === undefined) {
    throw new Error("useInvitation must be used within an InvitationProvider")
  }
  return context
}

export const useAcceptInvitation = (options: UseAcceptInvitationOptions = {}) => {
  const { invitation, clearInvitation } = useInvitation()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [invitationAccept, { loading }] = useSafeMutation(INVITATION_ACCEPT_MUTATION)
  const { onSuccess, onError, mode = "standard", redirectOnError = false } = options

  const acceptInvitation = useCallback(async () => {
    if (!invitation?.id) return

    const result = await invitationAccept({
      variables: {
        input: {
          invitationId: invitation.id,
        },
      },
    })

    if (result.data?.invitationAccept) {
      // Success case
      const inviterName = result.data.invitationAccept.contact?.owner?.fullName || "the inviter"

      if (onSuccess) {
        onSuccess(result.data.invitationAccept, inviterName)
      } else {
        // Default success behavior
        toast({
          title: "Invitation Accepted!",
          description: `You've been added to the contacts of ${inviterName}.`,
        })
        clearInvitation()
        navigate(contactsPath({}))
      }
    } else if (result.errors) {
      const errorDescription = getGraphqlErrorMessage(result.errors as any)

      if (onError) {
        onError(result.errors)
      } else {
        toast({
          title: "Error",
          description: errorDescription,
          variant: "destructive",
        })
      }

      if (redirectOnError) {
        navigate(contactsPath({}))
      }
    }
  }, [
    invitation,
    invitationAccept,
    toast,
    clearInvitation,
    navigate,
    onSuccess,
    onError,
    mode,
    redirectOnError,
  ])

  return {
    acceptInvitation,
    loading,
  }
}
